/**
 * EndOwnershipModal — Tier 0 slice-local component (SCRUM-172).
 *
 * Ends the current ownership. Mirrors the `UnitStatusModal` dialog
 * contract: scrim + `role="dialog"` + `useFocusTrap` (initial focus on
 * Cancel) + Esc-to-close + inline `role="alert"` on failure + a polite
 * SR announcement on success. The only field is an optional end date
 * (BE defaults to "now" when omitted). Requires the current ownership's
 * `version` for the If-Match optimistic-lock header (handled by the
 * mutation hook).
 */

import * as React from 'react';
import { Button, TextField } from '@sparta/ui-kit';
import type { EndOwnershipInput } from '@artemis/validators';
import { FilterSel } from './FilterSel.js';
import { dateOnlyToIso } from './ownershipDates.js';
import { useFocusTrap } from '../../../shared/hooks/useFocusTrap.js';
import { useSrAnnouncement } from '../../../shared/hooks/useSrAnnouncement.js';
import { useEndOwnership } from '../api/use-ownership-mutations.js';
import type { OwnershipView } from '../api/types.js';

export interface EndOwnershipModalProps {
  readonly propertyId: string;
  readonly ownership: OwnershipView;
  readonly onClose: () => void;
}

type EndedReason = EndOwnershipInput['endedReason'];

/** Reason options (value matches the BE enum; label is human copy). */
const REASON_OPTIONS: ReadonlyArray<{ id: EndedReason; label: string }> = [
  { id: 'sold', label: 'Sold' },
  { id: 'transferred', label: 'Transferred' },
  { id: 'property_archived', label: 'Property archived' },
  { id: 'owner_deactivated', label: 'Owner deactivated' },
  { id: 'expired', label: 'Expired' },
];

/** Local date input (yyyy-mm-dd) → ISO date-time, or undefined when blank. */
export function EndOwnershipModal({
  propertyId,
  ownership,
  onClose,
}: EndOwnershipModalProps): React.ReactElement {
  const [endedAt, setEndedAt] = React.useState('');
  const [reason, setReason] = React.useState<EndedReason>('sold');
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const { mutateAsync, status, errorMessage, reset } = useEndOwnership();
  const sr = useSrAnnouncement();

  useFocusTrap(dialogRef, { initialFocusRef: cancelRef });

  React.useEffect(() => {
    const handleKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleSave = async (): Promise<void> => {
    const iso = dateOnlyToIso(endedAt);
    try {
      await mutateAsync({
        propertyId,
        ownershipId: ownership.id,
        version: ownership.version,
        body: {
          endedReason: reason,
          ...(iso !== undefined ? { endedAt: iso } : {}),
        },
      });
      sr.polite('Ownership ended');
      onClose();
    } catch {
      // errorMessage drives the inline role="alert"; modal stays open.
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: 'var(--bg-overlay)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {sr.regions}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="end-ownership-title"
        aria-describedby="end-ownership-subtitle"
        className="w-full max-w-md rounded-2xl p-6"
        style={{ background: 'var(--bg-surface)', boxShadow: 'var(--shadow-xl)' }}
      >
        <h2
          id="end-ownership-title"
          className="m-0 text-lg font-semibold"
          style={{ color: 'var(--fg-primary)' }}
        >
          End ownership — {ownership.ownerName}
        </h2>
        <p
          id="end-ownership-subtitle"
          className="mt-1 mb-4 text-sm"
          style={{ color: 'var(--fg-secondary)' }}
        >
          Close this ownership record. Leave the date blank to end it
          today. History is preserved.
        </p>

        <div className="mb-3 flex flex-col gap-1.5">
          <span
            className="text-xs font-bold uppercase"
            style={{ color: 'var(--fg-tertiary)', letterSpacing: '0.05em' }}
          >
            Reason
          </span>
          <FilterSel<NonNullable<EndedReason>>
            label="Reason for ending ownership"
            value={reason ?? 'sold'}
            options={REASON_OPTIONS as ReadonlyArray<{ id: NonNullable<EndedReason>; label: string }>}
            onChange={(next) => {
              if (errorMessage !== null) reset();
              setReason(next);
            }}
          />
        </div>

        <TextField
          label="End date (optional)"
          type="date"
          value={endedAt}
          onChange={(e) => {
            if (errorMessage !== null) reset();
            setEndedAt(e.target.value);
          }}
        />

        {errorMessage !== null ? (
          <div
            role="alert"
            className="mt-4 rounded-lg p-3 text-sm"
            style={{
              background:
                'color-mix(in oklab, var(--danger-500) 12%, var(--bg-surface))',
              color: 'var(--fg-danger-on-tint)',
              border:
                '1px solid color-mix(in oklab, var(--danger-500) 30%, transparent)',
            }}
          >
            {errorMessage}
          </div>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <Button ref={cancelRef} variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              void handleSave();
            }}
            disabled={status === 'pending'}
          >
            {status === 'pending' ? 'Ending…' : 'End ownership'}
          </Button>
        </div>
      </div>
    </div>
  );
}
